import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, History } from "lucide-react";
import PeopleSearchSearchBar from "./PeopleSearchSearchBar.tsx";
import styles from "./PeopleSearchSearchBarWithDropdown.module.scss";
import type { HistoryEntry } from "../../hooks/useSearchHistory.ts";

export type SearchSuggestion = {
  id: number | string;
  displayName: string;
};

type PeopleSearchSearchBarWithDropdownProps = {
  value: string;
  onChange: (value: string) => void;
  onSearchCommit: (term: string) => void;
  suggestions: SearchSuggestion[];
  suggestionsLoading: boolean;
  history: HistoryEntry[];
  onAddToHistory: (term: string) => void;
  onClearHistory?: () => void;
  useInterpretSearch?: boolean;
  onUseInterpretSearchChange?: (active: boolean) => void;
  placeholder?: string;
  size?: "default" | "large";
  minCharsForSuggestions?: number;
};

const MIN_CHARS_NAME = 3;
const MIN_CHARS_DESCRIPTION = 2;

const PeopleSearchSearchBarWithDropdown: React.FC<PeopleSearchSearchBarWithDropdownProps> = ({
  value,
  onChange,
  onSearchCommit,
  suggestions,
  suggestionsLoading,
  history,
  onAddToHistory,
  onClearHistory,
  useInterpretSearch = false,
  onUseInterpretSearchChange,
  placeholder = "Buscar por nombre o empresa…",
  size = "default",
  minCharsForSuggestions,
}) => {
  const minChars = minCharsForSuggestions ?? (useInterpretSearch ? MIN_CHARS_DESCRIPTION : MIN_CHARS_NAME);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const placeholderByMode = useInterpretSearch
    ? "Ej: electricista en CABA, maderera en San Justo"
    : placeholder;

  const handleCommit = useCallback(
    (term: string) => {
      const trimmed = term.trim();
      if (trimmed) {
        onChange(trimmed);
        onAddToHistory(trimmed);
        onSearchCommit(trimmed);
      }
      setIsOpen(false);
    },
    [onChange, onAddToHistory, onSearchCommit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleCommit(value);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    },
    [value, handleCommit]
  );

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClearHistory = useCallback(() => {
    onClearHistory?.();
  }, [onClearHistory]);

  const trimmed = value.trim();
  const displayableHistory = useMemo(
    () => (useInterpretSearch ? history : history.filter((e) => !e.fromInterpret)),
    [history, useInterpretSearch]
  );
  const showDropdown = isOpen && (displayableHistory.length > 0 || suggestions.length > 0 || suggestionsLoading);
  const filteredHistory = useMemo(
    () =>
      trimmed
        ? displayableHistory
            .filter((e) => e.term.toLowerCase().includes(trimmed.toLowerCase()))
            .slice(0, 8)
        : displayableHistory.slice(0, 8),
    [displayableHistory, trimmed]
  );
  const showHistorySection = filteredHistory.length > 0;
  const showSuggestionsSection = trimmed.length >= minChars;

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${isOpen ? styles.container_open : ""}`}
    >
      {onUseInterpretSearchChange && (
        <div className={styles.toggle} role="group" aria-label="Modo de búsqueda">
          <div
            className={styles.toggleThumb}
            aria-hidden
            style={{ transform: useInterpretSearch ? "translateX(100%)" : "translateX(0)" }}
          />
          <button
            type="button"
            className={`${styles.toggleOption} ${!useInterpretSearch ? styles.toggleOption_active : ""}`}
            onClick={() => onUseInterpretSearchChange(false)}
            aria-pressed={!useInterpretSearch}
          >
            Por nombre
          </button>
          <button
            type="button"
            className={`${styles.toggleOption} ${useInterpretSearch ? styles.toggleOption_active : ""}`}
            onClick={() => onUseInterpretSearchChange(true)}
            aria-pressed={useInterpretSearch}
          >
            Por descripción con IA
          </button>
        </div>
      )}
      <div
        className={styles.inputWrapper}
        onFocus={() => setIsOpen(true)}
      >
        <PeopleSearchSearchBar
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholderByMode}
          size={size}
        />
      </div>
      {useInterpretSearch && (
        <p className={styles.exampleHint}>ej: electricista en CABA, maderera en San Justo</p>
      )}
      {showDropdown && (
        <div className={styles.dropdown}>
          {onClearHistory && displayableHistory.length > 0 && showHistorySection && (
            <button
              type="button"
              className={styles.clearHistory}
              onClick={(e) => {
                e.stopPropagation();
                handleClearHistory();
              }}
            >
              Limpiar historial
            </button>
          )}
          <ul className={styles.list}>
            {showHistorySection &&
              filteredHistory.map((entry, idx) => (
                <li key={`h-${entry.term}-${idx}`}>
                  <button
                    type="button"
                    className={styles.item}
                    onClick={() => {
                      onChange(entry.term);
                      handleCommit(entry.term);
                    }}
                  >
                    <History size={16} className={styles.itemIcon} />
                    {entry.term}
                  </button>
                </li>
              ))}
            {showSuggestionsSection &&
              (suggestionsLoading ? (
                <li className={styles.loadingItem}>
                  <span className={styles.loadingSpinner} />
                  Buscando…
                </li>
              ) : (
                suggestions.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={styles.item}
                      onClick={() => handleCommit(s.displayName)}
                    >
                      <Search size={16} className={styles.itemIcon} />
                      {s.displayName}
                    </button>
                  </li>
                ))
              ))}
          </ul>
          {showSuggestionsSection && !suggestionsLoading && suggestions.length === 0 && (
            <div className={styles.empty}>No hay coincidencias</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PeopleSearchSearchBarWithDropdown;
